import {
  APIComponentAttributes,
  APIEntityType,
  ArdoqId,
} from '@ardoq/api-types';
import {
  FieldType,
  componentAttributesMap,
  getComponentTypeByComponentId,
  getEntityById,
  getFieldLabel,
  readRawValue,
} from '@ardoq/renderers';
import { isTruthy } from '@ardoq/common-helpers';
import { type I18nLocale } from '@ardoq/i18n';
import {
  getComponentStylePlaceholder,
  defaultComponentStyleAttributeNames,
} from 'appModelStateEdit/propertiesEditor/componentStylePropertiesEditor/consts';
import { pick } from 'lodash';
import { getValidationErrorMessages } from 'scopeData/editors/validators';
import { DirtyAttributes } from 'appModelStateEdit/types';
import { getDefaultCSSColor } from '@ardoq/color-helpers';
import type { EnhancedScopeData, ScenarioModeState } from '@ardoq/data-model';
import { PermissionContext } from '@ardoq/access-control';
import { scenarioAccessControlInterface } from 'resourcePermissions/accessControlHelpers/scenarioAccessControlInterface';
import { componentAccessControlOperation } from 'resourcePermissions/accessControlHelpers/component';
import { activeScenarioOperations } from '@ardoq/core';
import { EditorProperty } from '../types';
import { getFeatureContext } from 'streams/featureContext$';

type DefaultStyle = {
  readonly icon: string | null;
  readonly image: string | null;
  readonly shape: string | null;
  readonly color: string | null;
};

const canEditStyle = (
  component: APIComponentAttributes,
  activeScenarioState: ScenarioModeState,
  permissionContext: PermissionContext
) => {
  if (activeScenarioOperations.isInScenarioMode(activeScenarioState)) {
    return scenarioAccessControlInterface.canEditActiveScenario(
      permissionContext,
      activeScenarioState
    );
  }
  return componentAccessControlOperation.canEditComponent({
    component,
    permissionContext,
    featureContext: getFeatureContext(),
  });
};

export const getDefaultStylePropertyValues = (
  componentId: ArdoqId,
  enhancedScopeData: EnhancedScopeData
) => {
  const componentType = getComponentTypeByComponentId(
    componentId,
    enhancedScopeData
  )!;
  const defaultStylePropertyValues = pick(
    componentType,
    defaultComponentStyleAttributeNames
  ) as DefaultStyle;
  if (!defaultStylePropertyValues.color) {
    return {
      ...defaultStylePropertyValues,
      color: getDefaultCSSColor(componentType.level),
    };
  }
  return defaultStylePropertyValues;
};

export type ComponentStyleImageOption = {
  readonly label: string;
  readonly value: string;
};

export const getDefaultProperties = (
  componentId: ArdoqId,
  activeScenarioState: ScenarioModeState,
  enhancedScopeData: EnhancedScopeData,
  dirtyAttributes: DirtyAttributes,
  permissionContext: PermissionContext,
  i18nLocale: I18nLocale,
  imageOptions: ComponentStyleImageOption[]
): EditorProperty[] => {
  const component = getEntityById(
    APIEntityType.COMPONENT,
    componentId,
    enhancedScopeData
  );
  if (!component) {
    return [];
  }
  const defaultStylePropertyValues = getDefaultStylePropertyValues(
    componentId,
    enhancedScopeData
  );
  return defaultComponentStyleAttributeNames
    .map(attributeName => {
      const type = componentAttributesMap.get(attributeName);
      if (!type) {
        return null;
      }
      const isColor = type === FieldType.COLOR;
      const label = getFieldLabel({
        fieldName: attributeName,
        enhancedScopeData,
        entityType: APIEntityType.COMPONENT,
      });
      const value = readRawValue(
        APIEntityType.COMPONENT,
        componentId,
        attributeName,
        enhancedScopeData
      );
      const defaultValue =
        defaultStylePropertyValues[attributeName as keyof DefaultStyle];
      const isDirty = dirtyAttributes.has(attributeName);
      const errorMessages = getValidationErrorMessages(type, value);
      const isDisabled = !canEditStyle(
        component,
        activeScenarioState,
        permissionContext
      );
      return {
        type,
        name: attributeName,
        label,
        value,
        defaultValue,
        isDirty,
        isDisabled,
        errorMessages,
        options: type === FieldType.IMAGE ? imageOptions : undefined,
        ...(isColor ? { hasResetButton: true } : {}),
        ...(!isColor && !value
          ? { placeholder: getComponentStylePlaceholder(i18nLocale) }
          : {}),
      };
    })
    .filter(isTruthy);
};
